"use client";

import FairnessButton from "@casino/ui/Games/FairnessButton";
import FavouriteButton from "@casino/ui/Games/FavouriteButton";
import GameCurrencySelect from "@casino/ui/Games/GameCurrencySelect";
import { useGamePageData } from "@casino/ui/Games/hooks";
import LikeButton from "@casino/ui/Games/LikeButton";
import LoginAreaPopover from "@casino/ui/Games/LoginAreaPopover";
import LoginIntro from "@casino/ui/Games/LoginIntro";
import ModeToggle from "@casino/ui/Games/ModeToggle";
import MuteButton from "@casino/ui/Games/MuteButton";
import RotateScreenModal from "@casino/ui/Games/RotateScreenModal";
import RulesButton from "@casino/ui/Games/RulesButton";
import type { VotesQuery } from "@workspace/casino_core/queries/VotesQuery";
import { cn } from "@workspace/frontend-ui";
import type { ComponentProps, FC, ReactNode } from "react";
import GameControlsExpand from "../ExpandButton";
import GameControlsFullscreen from "../FullscreenButton";
import GameControlsInfo, { GameStatsAccordion } from "../InfoButton";

type Props = ComponentProps<"div"> & {
  game?: SingleGameFragment;
  sideBar: ReactNode;
  gameInfo: ReactNode;
  promoBanners?: ReactNode;
  likesData?: VotesQuery;
  isFavourite?: boolean;
  category?: string;
  isWebComponentGame?: boolean; // This actually trigger the logic.
  isWebComponentGameSlug?: boolean; // This trigger just the UI.
  isLiveRtpEnabled?: boolean;
};

const GameHud: FC<Props> = ({
  sideBar,
  promoBanners,
  likesData,
  isFavourite,
  gameInfo,
  children,
  game,
  category,
  isWebComponentGame: isWebComponentGameLogic = false,
  isWebComponentGameSlug = false,
  isLiveRtpEnabled = false,
}) => {
  const { view, isAuthModalVisible, isFunMode, gameBoxRef } = useGamePageData();

  // This combines the logic, one is visual the other is logically.
  const isWebComponentGame = isWebComponentGameSlug || isWebComponentGameLogic;

  return (
    <>
      <div
        data-view={view}
        className={cn(
          "group/shell grid gap-4 w-full",
          isWebComponentGame
            ? "min-[1513px]:grid-cols-[minmax(0,1fr)_386px]"
            : "xl:grid-cols-[minmax(0,1fr)_386px] data-[view=expanded]:xl:grid-cols-1 data-[view=fullscreen]:xl:grid-cols-1",
        )}
      >
        <main
          className={cn(
            "min-w-0",
            isWebComponentGame ? "min-[1513px]:pb-20" : "xl:pb-20",
          )}
        >
          <div ref={gameBoxRef} className="p-3 bg-secondary rounded-lg">
            {children}
            <div className="flex items-center mr-1 justify-between pt-2 gap-x-2 group-data-[view=fullscreen]/shell:hidden">
              {!isWebComponentGame && (
                <ModeToggle hasFunMode={game?.hasFunMode} />
              )}
              {isWebComponentGame && isWebComponentGameLogic && (
                <FairnessButton />
              )}
              <div className="flex-grow" />
              <LoginIntro>
                <LikeButton
                  key={game?.id}
                  gameId={game?.id}
                  likes={likesData?.casino?.votes?.clapps}
                  isLiked={likesData?.casino?.myVote?.myRating === "LIKE"}
                />
              </LoginIntro>
              {game && (
                <div className="contents max-[420px]:hidden!">
                  <GameControlsInfo
                    game={game}
                    isLiveRtpEnabled={isLiveRtpEnabled}
                  />
                </div>
              )}
              {isWebComponentGame && isWebComponentGameLogic && <RulesButton />}
              {isWebComponentGame && isWebComponentGameLogic && <MuteButton />}
              {!isWebComponentGame && (
                <LoginIntro hidden={isFunMode}>
                  <GameCurrencySelect />
                </LoginIntro>
              )}
              <LoginIntro>
                <FavouriteButton
                  key={game?.id}
                  gameId={game?.id}
                  isFavourite={isFavourite}
                />
              </LoginIntro>
              {!isWebComponentGame && <GameControlsExpand />}
              {!isWebComponentGame && <GameControlsFullscreen />}
            </div>
          </div>
          {game && (
            <div className="hidden w-full max-[420px]:block! mt-4 group-data-[view=fullscreen]/shell:hidden">
              <GameStatsAccordion
                game={game}
                isLiveRtpEnabled={isLiveRtpEnabled}
              />
            </div>
          )}
          <div className="group-data-[view=fullscreen]/shell:hidden">
            {promoBanners}
          </div>
          <div className="mt-4 group-data-[view=fullscreen]/shell:hidden">
            {gameInfo}
          </div>
        </main>
        <aside
          className={cn(
            "group-data-[view=expanded]/shell:hidden group-data-[view=fullscreen]/shell:hidden self-start min-w-0 no-scrollbar",
            isWebComponentGame
              ? "min-[1513px]:h-[calc(100svh-6rem)] min-[1513px]:overflow-y-scroll"
              : "xl:h-[calc(100svh-6rem)] xl:overflow-y-scroll",
          )}
        >
          {sideBar}
        </aside>
      </div>
      <LoginAreaPopover open={isAuthModalVisible} />
      <RotateScreenModal category={category} />
    </>
  );
};

export default GameHud;
